import { useMsal } from '@azure/msal-react';
import { Box, IconButton, Button as MaterialButton, Skeleton, Tooltip, Typography } from '@mui/material';
import { PreviewProspectDetailsDto, ProspectDataDto, ProspectDataExperienceDto } from '@zaplify/prospects';
import { apiEndpoint, getZaplifySdk } from '@zaplify/sdk';
import { Briefcase, Building, Linkedin, Mail, MapPin, NotepadText, Tag, UsersRound, X } from 'lucide-react';
import { ReactElement, useEffect, useState } from 'react';
import { getEmailState } from '../../../../helpers/utils';

function getExperienceDateRange(experience: ProspectDataExperienceDto) {
    let dateString = '';
    if (experience.occupationStartDate) {
        dateString += experience.occupationStartDate + ' - ';
    }
    if (experience.occupationEndDate) {
        dateString += experience.occupationEndDate;
    } else {
        dateString += 'Present';
    }

    return dateString;
}

export const ProspectsPanel = ({
    prospect,
    prospectIsLoading,
    isPreview,
    onClose,
}: {
    prospect: ProspectDataDto;
    prospectIsLoading: boolean;
    isPreview: boolean;
    onClose: () => void;
}) => {
    const [displayEditModal, setDisplayEditModal] = useState(false);
    const [prospectDetails, setProspectDetails] = useState<PreviewProspectDetailsDto | null>(null);
    const [accountDetails, setAccountDetails] = useState<any | null>(null);
    const [loadingProspectDetails, setLoadingProspectDetails] = useState(false);
    const msal = useMsal();

    const getEmailText = (prospect: PreviewProspectDetailsDto): string => {
        if (prospectIsLoading) return '...';
        const emailState = getEmailState(prospect?.email);
        let emailText = prospect?.email?.toLowerCase();
        if (emailState === 'Email missing') {
            emailText = 'Not enriched yet';
        } else if (emailState === 'Enrichment') {
            emailText = 'Looking for email...';
        }
        return emailText;
    };

    const getEmailTooltip = (prospect: PreviewProspectDetailsDto): string => {
        if (prospectIsLoading) return 'Loading';
        const emailState = getEmailState(prospect?.email);
        let emailText = prospect?.email?.toLowerCase();
        if (emailState === 'Email missing') {
            emailText = 'We could not find this email address, you can always enter it manually.';
        } else if (emailState === 'Enrichment') {
            emailText =
                'Email is still enriching, this could take a day or two. You can always snooze the contact for later.';
        }
        return emailText;
    };

    const handleOpenEditProspectModal = () => {
        setDisplayEditModal(true);
    };

    const handleClose = () => {
        setDisplayEditModal(false);
    };

    const handleEdit = () => {
        setDisplayEditModal(true);
        handleClose();
    };

    const handleProspectData = async (personId) => {
        try {
            setLoadingProspectDetails(true);
            const prospectSdk = getZaplifySdk(apiEndpoint, msal.instance).profiles;
            const response = await prospectSdk.sources.previewProspectingDetails({ personId });
            setProspectDetails(response);
        } finally {
            setLoadingProspectDetails(false);
        }
    };

    useEffect(() => {
        if (prospect?.personId) handleProspectData(prospect.personId);
    }, [prospect?.personId]);

    return (
        <Box
            className="prospects-panel"
            sx={{
                display: 'flex',
                width: '307px',
                padding: '8px',
                flexDirection: 'column',
                alignItems: 'center',
                borderLeft: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                background: 'var(--grey-palette-white, #FFF)',
                height: '98vh',
                overflow: 'auto',
                paddingBottom: '16px',
                boxSizing: 'border-box',

                border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                boxShadow: '0px 1px 10px 0px rgba(30, 36, 48, 0.10), 0px 8px 16px 0px rgba(30, 36, 48, 0.08)',
                borderRadius: 'var(--radius-xl, 14px)',
                '& svg': {
                    color: '#6B7985',
                },
                '& p': {
                    color: '#3A4B59',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    padding: '8px var(--S-s-regular, 8px)',
                    alignItems: 'center',
                    gap: 'var(--M-m-compact, 16px)',
                    alignSelf: 'stretch',
                    borderBottom: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                    justifyContent: 'space-between',
                }}
            >
                <Typography sx={{ fontWeight: 500, color: '#23323F', fontSize: '17px' }}>
                    {prospect?.fullName}
                </Typography>
                <IconButton onClick={onClose}>
                    <X size={20} strokeWidth={1} />
                </IconButton>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    padding: 'var(--S-s-spacious, 12px) var(--S-s-regular, 8px)',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 'var(--M-m-compact, 16px)',

                    alignSelf: 'stretch',
                    background: 'var(--grey-palette-white, #FFF)',
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                    <Typography
                        id="contact-details-text"
                        variant="subtitle1"
                        sx={{ fontWeight: 500, fontSize: '15px', color: '#23323F' }}
                    >
                        Contact details
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            '& p': {
                                fontSize: '14px',
                            },
                            width: '100%',
                        }}
                    >
                        <Briefcase size={20} strokeWidth={1} />
                        <Typography
                            variant="body1"
                            sx={{
                                '& > span': {
                                    fontWeight: 500,
                                },
                            }}
                        >
                            <span>{prospect?.occupationTitle}</span> at <span>{prospect?.organizationName}</span>
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            '& p': {
                                fontSize: '14px',
                            },
                        }}
                    >
                        <MapPin size={20} strokeWidth={1} />
                        <Typography variant="body1">{prospect?.location}</Typography>
                    </Box>
                    {isPreview ? (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                width: '100%',
                                '& > svg': {
                                    flexShrink: 0,
                                },
                            }}
                        >
                            <Mail size={20} strokeWidth={1} />
                            <Box
                                sx={{
                                    display: 'flex',
                                    padding: 'var(--S-s-regular, 8px)',
                                    borderRadius: 'var(--radius-round, 100px)',
                                    background: 'var(--grey-palette-grey-1, #F7F8FA)',
                                    alignItems: 'flex-start',
                                    gap: '3px',
                                    fontSize: '14px',
                                    wordBreak: 'break-all',
                                    minWidth: 0,
                                    '& > div': {
                                        width: 'var(--S-2xs, 4px)',
                                        height: 'var(--S-2xs, 4px)',
                                        background: 'var(--grey-palette-grey-4, #969FAC)',
                                        borderRadius: '50%',
                                    },
                                }}
                            >
                                {getEmailText(prospect)}
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                width: '100%',
                                '& > svg': {
                                    flexShrink: 0,
                                },
                            }}
                        >
                            <Mail size={20} strokeWidth={1} />
                            <Tooltip title={getEmailTooltip(prospect)} placement="bottom">
                                <Typography
                                    variant="body1"
                                    color="primary"
                                    sx={{
                                        fontSize: '14px',
                                        wordBreak: 'break-all',
                                        minWidth: 0,
                                    }}
                                >
                                    {getEmailText(prospect)}
                                </Typography>
                            </Tooltip>
                        </Box>
                    )}
                    {isPreview ? (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                width: '100%',

                                '& > a >p': {
                                    fontSize: '14px',
                                    width: 200,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: '#6583EF',
                                },
                                '& a': {
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                },
                                '& span': {
                                    marginLeft: 'auto',
                                },
                            }}
                        >
                            <Linkedin size={20} strokeWidth={1} />

                            <Box
                                sx={{
                                    display: 'flex',
                                    padding: 'var(--S-s-regular, 8px)',
                                    borderRadius: 'var(--radius-round, 100px)',
                                    background: 'var(--grey-palette-grey-1, #F7F8FA)',
                                    alignItems: 'flex-start',
                                    gap: '3px',
                                    '& > div': {
                                        width: 'var(--S-2xs, 4px)',
                                        height: 'var(--S-2xs, 4px)',
                                        background: 'var(--grey-palette-grey-4, #969FAC)',
                                        borderRadius: '50%',
                                    },
                                }}
                            >
                                {prospect?.linkedinProfileUrl?.replace(/(https:\/\/)?(www\.)?/i, '') || '...'}
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                width: '100%',

                                '& > a >p': {
                                    fontSize: '14px',
                                    width: 200,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: '#6583EF',
                                },
                                '& a': {
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                },
                                '& span': {
                                    marginLeft: 'auto',
                                },
                            }}
                        >
                            <Linkedin size={20} strokeWidth={1} />
                            <a
                                href={prospect?.linkedinProfileUrl ? prospect?.linkedinProfileUrl : ''}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Typography variant="body1" color="primary">
                                    {prospect?.linkedinProfileUrl?.replace(/(https:\/\/)?(www\.)?/i, '') || '...'}
                                </Typography>
                            </a>
                        </Box>
                    )}
                </Box>
            </Box>
            {(prospectIsLoading || Boolean(prospectDetails?.experiences?.length)) && (
                <Box
                    sx={{
                        display: 'flex',
                        padding: 'var(--S-s-spacious, 12px) var(--S-s-regular, 8px)',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 'var(--M-m-compact, 16px)',

                        alignSelf: 'stretch',
                        background: 'var(--grey-palette-white, #FFF)',
                    }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '15px', color: '#23323F' }}>
                        Experience
                    </Typography>
                    <CollapsableExperiences
                        currentOrganization={prospect?.organizationName}
                        experiences={prospectDetails?.experiences}
                        icon={<Briefcase size={20} strokeWidth={1} />}
                        loading={prospectIsLoading}
                    />
                </Box>
            )}
            <Box
                sx={{
                    display: 'flex',
                    padding: 'var(--S-s-spacious, 12px) var(--S-s-regular, 8px)',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 'var(--M-m-compact, 16px)',

                    alignSelf: 'stretch',
                    background: 'var(--grey-palette-white, #FFF)',
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '15px', color: '#23323F' }}>
                    Company
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            '& p': {
                                fontSize: '15px',
                                width: 250,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontWeight: 500,
                            },
                            '& a': {
                                textDecoration: 'none',
                                display: 'block',
                                width: '80%',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            },
                            '& img': {
                                width: '20px',
                                height: '20px',
                            },
                        }}
                    >
                        <img
                            className="image"
                            onError={(e) => {
                                (e.target as HTMLImageElement).onerror = null;
                                (e.target as HTMLImageElement).src = '/images/no-logo-icon.svg';
                            }}
                            src={
                                prospect?.organizationDomain
                                    ? `https://logo.clearbit.com/${prospect?.organizationDomain}`
                                    : '/images/no-logo-icon.svg'
                            }
                            alt="Company logo"
                        />
                        <a
                            href={prospect?.organizationDomain ? `https://${prospect?.organizationDomain}` : ''}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {prospectIsLoading ? (
                                <Skeleton height={24} />
                            ) : (
                                <Typography variant="body1" color="primary">
                                    {' '}
                                    {prospect?.organizationName}
                                </Typography>
                            )}
                        </a>
                    </Box>
                    {(prospectIsLoading || prospect?.industry) && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                '& p': {
                                    fontSize: '14px',
                                    width: 250,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                },
                                '& a': {
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                },
                            }}
                        >
                            <Building size={20} strokeWidth={1} />
                            {prospectIsLoading ? (
                                <Skeleton width="80%" height={24} />
                            ) : (
                                <Typography variant="body1">{prospect?.industry}</Typography>
                            )}
                        </Box>
                    )}
                    {(prospectIsLoading || prospect?.organizationSize) && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                '& p': {
                                    fontSize: '14px',
                                    width: 250,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                },
                                '& a': {
                                    textDecoration: 'none',
                                },
                            }}
                        >
                            <UsersRound size={20} strokeWidth={1} />
                            {prospectIsLoading ? (
                                <Skeleton width="80%" height={24} />
                            ) : (
                                <Typography variant="body1">{prospect?.organizationSize} employees</Typography>
                            )}
                        </Box>
                    )}
                    {
                        <CollapsibleText
                            data={accountDetails?.aboutUs}
                            icon={<NotepadText size={20} strokeWidth={1} />}
                            loading={accountDetails?.status == 'loading'}
                        />
                    }
                    {
                        <CollapsibleTags
                            data={accountDetails?.specialties}
                            icon={<Tag size={20} strokeWidth={1} />}
                            loading={accountDetails?.status == 'loading'}
                        />
                    }
                    {prospect?.organizationLinkedinUrl && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                '& > a > p': {
                                    fontSize: '14px',
                                    width: 250,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: '#6583EF',
                                },
                                '& a': {
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                },
                            }}
                        >
                            <Linkedin size={20} strokeWidth={1} />
                            <a
                                href={prospect?.organizationLinkedinUrl ? prospect?.organizationLinkedinUrl : ''}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Typography variant="body1" color="primary">
                                    {prospect?.organizationLinkedinUrl}
                                </Typography>
                            </a>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

const CollapsibleText = ({ data, icon, loading }: { data?: string; icon?: ReactElement; loading?: boolean }) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            {(loading || data) && (
                <Box>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'start',
                            justifyContent: 'start',
                            marginBottom: '4px',
                            '& p': {
                                fontSize: '14px',
                                width: 250,
                                display: '-webkit-box',
                                WebkitLineClamp: open ? 999 : 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            },
                        }}
                    >
                        {icon}
                        {loading && <Skeleton width="80%" height={24} />}
                        {data && <Typography variant="body1">{data}</Typography>}
                    </Box>
                    {data && (
                        <MaterialButton
                            sx={{ marginLeft: '20px', height: '26px' }}
                            onClick={() => setOpen((state) => !state)}
                            variant="text"
                        >
                            {open ? 'Show less' : 'Show more'}
                        </MaterialButton>
                    )}
                </Box>
            )}
        </>
    );
};

const CollapsibleTags = ({ data, icon, loading }: { data?: string[]; icon?: ReactElement; loading: boolean }) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            {(loading || Boolean(data?.length)) && (
                <Box>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'start',
                            justifyContent: 'start',
                            marginBottom: '4px',
                            '& svg': {
                                minWidth: '20px',
                            },
                        }}
                    >
                        {icon}
                        {loading && (
                            <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {[...Array(5)].map((_, index) => (
                                    <Skeleton
                                        key={index}
                                        width={100}
                                        height={24}
                                        sx={{ borderRadius: 'var(--radius-round, 100px)' }}
                                    />
                                ))}
                            </Box>
                        )}
                        {Boolean(data?.length) && (
                            <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {data?.slice(0, open ? 99 : 4).map((tag, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            padding: 'var(--S-2xs, 4px) var(--S-s-regular, 8px)',
                                            alignItems: 'center',
                                            gap: '6px',
                                            borderRadius: 'var(--radius-round, 100px)',
                                            background: 'var(--grey-palette-grey-1, #F7F8FA)',
                                            color: 'var(--grey-palette-grey-5, #6B7985)',
                                            fontSize: '11px',
                                            fontWeight: 500,
                                        }}
                                    >
                                        {tag}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                    {Boolean(data?.length) && data?.length > 4 && (
                        <MaterialButton
                            sx={{ marginLeft: '20px', height: '26px' }}
                            onClick={() => setOpen((state) => !state)}
                            variant="text"
                        >
                            {open ? 'Show less' : 'Show more'}
                        </MaterialButton>
                    )}
                </Box>
            )}
        </>
    );
};

function getProspectExperiencesSorted(a: ProspectDataExperienceDto, b: ProspectDataExperienceDto) {
    if (!a.occupationEndDate && !b.occupationEndDate) {
        return new Date(b.occupationStartDate || 0).valueOf() - new Date(a.occupationStartDate || 0).valueOf();
    }
    if (!a.occupationEndDate) {
        return -1;
    }
    if (!b.occupationEndDate) {
        return 1;
    }
    return new Date(b.occupationEndDate || 0).valueOf() - new Date(a.occupationEndDate || 0).valueOf();
}

function moveCurrentOrgToFront(experiences: ProspectDataExperienceDto[], currentOrganization?: string) {
    if (!currentOrganization) return experiences;
    const currentOrgIndex = experiences.findIndex(
        (experience) => experience.organizationName?.toLowerCase() === currentOrganization?.toLowerCase()
    );
    if (currentOrgIndex === -1) return experiences;
    const currentOrg = experiences[currentOrgIndex];
    experiences.splice(currentOrgIndex, 1);
    experiences.unshift(currentOrg);
    return experiences;
}

const CollapsableExperiences = ({
    currentOrganization,
    experiences,
    icon,
    loading,
}: {
    experiences?: ProspectDataExperienceDto[];
    icon?: ReactElement;
    currentOrganization?: string;
    loading?: boolean;
}) => {
    const [open, setOpen] = useState(false);
    //Checking if it exists
    if (!experiences?.length) return null;

    const defaultShowNumberOfExperiences = 2;
    const sortedExperiences = experiences.sort(getProspectExperiencesSorted);
    const sortedExperiencesWithCurrentOrgFirst = moveCurrentOrgToFront(sortedExperiences, currentOrganization);
    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '4px',
                    alignItems: 'center',
                    '& svg': {
                        color: '#6B7985',
                        marginTop: '2px',
                        minWidth: '20px',
                    },
                    flexGrow: 1,
                }}
            >
                {icon}
                <Box sx={{ display: 'flex', flexGrow: 1, gap: '8px', flexDirection: 'column' }}>
                    {loading ? (
                        <Skeleton variant="rectangular" width="100%" />
                    ) : (
                        sortedExperiencesWithCurrentOrgFirst
                            ?.slice(0, open ? 12 : defaultShowNumberOfExperiences)
                            .map((experience, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        '& > .job-text > span': {
                                            textTransform: 'capitalize',
                                        },
                                        '& > .date-range-text': {
                                            color: '#6B7985',
                                            fontSize: '13px',
                                        },
                                        '& a': {
                                            color: '#6583EF',
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        },
                                    }}
                                >
                                    <Typography
                                        className="job-text"
                                        variant="body1"
                                        sx={{ fontSize: '14px', fontWeight: 400 }}
                                    >
                                        <span>{experience.occupationTitle}</span> at{' '}
                                        <span>
                                            {experience.organizationDomain ? (
                                                <a
                                                    href={'https://' + experience.organizationDomain}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    {experience.organizationName}
                                                </a>
                                            ) : (
                                                experience.organizationName
                                            )}
                                        </span>
                                    </Typography>
                                    <Typography
                                        className="date-range-text"
                                        variant="body2"
                                        sx={{ color: '#6B7985', fontSize: '13px' }}
                                    >
                                        {getExperienceDateRange(experience)}
                                    </Typography>
                                </Box>
                            ))
                    )}
                </Box>
            </Box>
            {Boolean(sortedExperiencesWithCurrentOrgFirst?.length > defaultShowNumberOfExperiences) && (
                <MaterialButton
                    sx={{ marginLeft: '20px', height: '26px' }}
                    onClick={() => setOpen((state) => !state)}
                    variant="text"
                >
                    {open ? 'Show less' : 'Show more'}
                </MaterialButton>
            )}
        </Box>
    );
};

const IconButtonSecondary = ({ children, onClick, sx }: { children?: ReactElement; onClick?: () => any; sx?: any }) => {
    return (
        <MaterialButton
            onClick={onClick}
            sx={{
                display: 'flex',

                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 'var(--radius-md, 8px)',
                border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                background: 'var(--grey-palette-white, #FFF)',
                boxShadow: '0px 1px 2px 0px rgba(20, 21, 26, 0.06)',
                boxSizing: 'border-box',
                minWidth: 0,
                color: '#3A4B59',
                height: '24px',
                width: '24px',
                padding: 0,
                '&:hover': {
                    background: 'var(--grey-palette-grey-1, #F7F8FA)',
                    color: '#3A4B59',
                    boxShadow: '0px 1px 2px 0px rgba(20, 21, 26, 0.06)',
                },
                ...sx,
            }}
        >
            {children}
        </MaterialButton>
    );
};
